import { useQuery, useMutation } from '@apollo/client';
import { Alert, Button, Spinner, Stack } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { formatMarkdownTimestamps } from '../../components/date_formatting/utils';
import {
  AnalyzeStatus,
  CreateTransportAnalysisInput,
  CreateTransportAnalysisMutation,
  CreateTransportAnalysisMutationVariables,
  TourAnalysesQuery,
  TourAnalysesQueryVariables,
} from '../../lib/graphql';
import { Tour } from '../entities';
import { createTransportAnalysisMutation, tourAnalysesQuery, tourQuery } from '../graphql';
import { useAnalyzeContext } from './analyze_context';

import styles from './tour_analyze_tab.module.css';

export const TourAnalyzeTab: React.FunctionComponent<{ tour: Tour }> = ({ tour }) => {
  const { isLoading, setIsLoading, error, setError } = useAnalyzeContext();
  const { timeZone } = useTimeZone();
  const [anythingToPoll, setAnythingToPoll] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [openIndices, setOpenIndices] = useState<Set<number>>(new Set([0]));

  const togglePanel = (index: number) => {
    setOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const [createAnalysis, { loading: createAnalysisLoading }] = useMutation<
    CreateTransportAnalysisMutation,
    CreateTransportAnalysisMutationVariables
  >(createTransportAnalysisMutation);

  const { refetch, data: queryData } = useQuery<TourAnalysesQuery, TourAnalysesQueryVariables>(tourAnalysesQuery, {
    variables: { tour_id: tour.tour_id },
    fetchPolicy: 'network-only',
    onCompleted: (data: TourAnalysesQuery) => {
      const analyses = data?.tour?.analyses || [];
      const hasActive = hasActiveAnalyses(analyses);

      if (document.hidden) {
        setAnythingToPoll(false);
      } else if (hasActive) {
        setAnythingToPoll(true);
      } else {
        setAnythingToPoll(false);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  const refetchRef = useRef<(() => void) | null>(null);
  const queryDataRef = useRef<TourAnalysesQuery | undefined>(undefined);
  refetchRef.current = refetch;
  queryDataRef.current = queryData;

  const hasActiveAnalyses = (
    analyses: Array<{ status: AnalyzeStatus } | null | undefined> | null | undefined,
  ): boolean => {
    if (!analyses) {
      return false;
    }
    const validAnalyses = analyses.filter(notNil);
    return (
      validAnalyses.length > 0 &&
      validAnalyses.some(
        (analysis) => analysis.status === AnalyzeStatus.NEW || analysis.status === AnalyzeStatus.IN_PROGRESS,
      )
    );
  };

  useEffect(() => {
    const initialAnalyses = tour.analyses || [];
    if (hasActiveAnalyses(initialAnalyses) && !document.hidden) {
      setAnythingToPoll(true);
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setAnythingToPoll(false);
      } else {
        const queryAnalyses = queryDataRef.current?.tour?.analyses;
        const currentAnalyses = queryAnalyses !== undefined ? queryAnalyses : tour.analyses || [];
        if (hasActiveAnalyses(currentAnalyses)) {
          setAnythingToPoll(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [tour.analyses]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (anythingToPoll && refetchRef.current && !document.hidden) {
      pollingIntervalRef.current = setInterval(() => {
        const queryAnalyses = queryDataRef.current?.tour?.analyses;
        const latestAnalyses = queryAnalyses !== undefined ? queryAnalyses : [];

        const stillHasActiveAnalyses = hasActiveAnalyses(latestAnalyses);

        if (!stillHasActiveAnalyses || document.hidden) {
          setAnythingToPoll(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }

        if (!document.hidden && refetchRef.current && stillHasActiveAnalyses) {
          refetchRef.current();
        }
      }, 5000);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [anythingToPoll]);

  const getCompanyId = (): string | null => {
    return tour.shipper?.company_id || tour.carrier?.company_id || null;
  };

  const handleAnalyzeClick = () => {
    const companyId = getCompanyId();

    if (!companyId) {
      setError('Unable to determine company ID. Please ensure tour has a shipper or carrier.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const input: CreateTransportAnalysisInput = {
      companyId,
      tourId: tour.tour_id.toString(),
    };

    createAnalysis({
      variables: { input },
      refetchQueries: [
        {
          query: tourQuery,
          variables: {
            tour_id: tour.tour_id,
          },
        },
        {
          query: tourAnalysesQuery,
          variables: {
            tour_id: tour.tour_id,
          },
        },
      ],
    })
      .then(() => {
        setIsLoading(false);
        const triggerRefetch = () => {
          if (refetchRef.current) {
            refetchRef.current();

            setTimeout(() => {
              const analyses = queryDataRef.current?.tour?.analyses || [];
              if (hasActiveAnalyses(analyses) && !document.hidden) {
                setAnythingToPoll(true);
              }
            }, 300);
          } else {
            setTimeout(triggerRefetch, 100);
          }
        };
        triggerRefetch();
      })
      .catch((err) => {
        setError(err.message || 'Failed to create transport analysis');
        setIsLoading(false);
      });
  };

  const rawAnalyses = (queryData?.tour?.analyses || tour.analyses || []).filter(notNil);

  const displayAnalyses = [...rawAnalyses].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const hasActiveAnalysesNow = hasActiveAnalyses(rawAnalyses);
  const showHeaderLoader = hasActiveAnalysesNow && !document.hidden;

  return (
    <div>
      <div className={styles.header}>
        <h3 className={styles.title}>Transport Analysis</h3>
        {(() => {
          if (showHeaderLoader) {
            return (
              <div className={styles.headerLoader}>
                <Spinner size="small" />
                <span className={styles.headerLoaderText}>Analyzing...</span>
              </div>
            );
          }

          const loading = createAnalysisLoading;
          return (
            <>
              <Button kind="primary" onClick={() => handleAnalyzeClick()} disabled={isLoading || loading}>
                Analyze
              </Button>
              {(isLoading || loading) && (
                <div className={styles.loadingOverlay}>
                  <Spinner />
                  <p className={styles.loadingOverlayText}>Analyzing transport...</p>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <h4>Error:</h4>
          <div className={styles.errorBox}>Failed to analyze transport: {error}</div>
        </div>
      )}

      <Alert kind="info" className={styles.infoAlert}>
        <p>
          This result has been generated by AI and may include incorrect information. Add your feedback in{' '}
          <a
            href="https://www.notion.so/23ec1b3dd25680688a9feae6f3210d68?pvs=25"
            target="_blank"
            rel="noopener noreferrer">
            Notion
          </a>{' '}
          or in{' '}
          <a href="https://trimble.enterprise.slack.com/archives/C0973K3PA1E" target="_blank" rel="noopener noreferrer">
            Slack
          </a>{' '}
          🙇.
        </p>
      </Alert>

      <div>
        {displayAnalyses.length > 0 && (
          <Stack gap="small" className={styles.accordion}>
            {displayAnalyses.map((analysis, index) => {
              const isOpen = openIndices.has(index);
              const isIncomplete =
                analysis.status === AnalyzeStatus.NEW || analysis.status === AnalyzeStatus.IN_PROGRESS;
              const showPollingLoader = showHeaderLoader && isIncomplete;

              return (
                <details key={analysis.analyzeId} open={isOpen} className={styles.accordionItem}>
                  <summary
                    onClick={(e) => {
                      e.preventDefault();
                      togglePanel(index);
                    }}
                    className={classnames(
                      styles.accordionTitle,
                      isOpen ? styles.accordionTitleActive : styles.accordionTitleInactive,
                    )}>
                    <div>
                      <span className={styles.statusSpan}>Status: {analysis.status}</span>
                      {showPollingLoader && (
                        <span className={styles.statusLoader}>
                          <Spinner size="small" />
                        </span>
                      )}
                      <span className={styles.createdSpan}>
                        Created: {DateTime.fromISO(analysis.createdAt).toFormat('dd.MM.yyyy HH:mm')}
                      </span>
                    </div>
                  </summary>
                  <div className={styles.accordionContent}>
                    {analysis.error && (
                      <div className={styles.errorMessage}>
                        <strong>Error:</strong> {analysis.error}
                      </div>
                    )}
                    {analysis.result && (
                      <div>
                        {analysis.result.startsWith('http') ? (
                          <a
                            href={analysis.result}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.resultLink}>
                            {analysis.result}
                          </a>
                        ) : (
                          <div className={classnames('markdown-content', styles.markdownContent)}>
                            <ReactMarkdown>{formatMarkdownTimestamps(analysis.result, { timeZone })}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </Stack>
        )}
      </div>
    </div>
  );
};
